<script lang="ts">
  import { onMount } from 'svelte';
  import { engine } from '../lib/player/engine.svelte.ts';
  import type { TileSlot } from '../lib/player/engine.svelte.ts';

  interface Props {
    tile: TileSlot;
    showStats: boolean;
  }

  const { tile, showStats }: Props = $props();

  /**
   * Position in the angle list, not in the grid. Slots rotate as the main stream
   * cycles, but the digit keys address angles, so a badge showing the slot index
   * would point at the wrong tile the moment you press an arrow.
   */
  const number = $derived(engine.visibleAngles.findIndex((a) => a.id === tile.angle.id) + 1);

  let container: HTMLDivElement;

  // The player attaches to this element and outlives every layout change, which
  // is what keeps promoting an angle free of a reload.
  onMount(() => {
    engine.mount(tile.angle.id, container);
    return () => engine.unmount(tile.angle.id);
  });

  const isMain = $derived(tile.slot === 'm');
  const hasAudio = $derived(engine.audioAngleId === tile.angle.id && !engine.globalMute);
  /** dB above this angle's own baseline. Only meaningful once it has one. */
  const erregung = $derived(engine.excitement[tile.angle.id] ?? 0);
  const erregt = $derived(erregung > 3);

  const offsetLabel = $derived(
    tile.angle.syncOffsetMs > 0 ? `+${(tile.angle.syncOffsetMs / 1000).toFixed(1)}s` : null,
  );
</script>

<div
  class="tile"
  class:main={isMain}
  class:hot={erregt}
  data-focusable
  tabindex="-1"
  role="button"
  aria-label={`${tile.angle.label} als Hauptstream setzen`}
  onfocus={() => engine.setFocused(tile.angle.id)}
  onclick={() => engine.setMain(tile.angle.id)}
  onkeydown={(e) => { if (e.key === 'Enter') engine.setMain(tile.angle.id); }}
>
  <div class="surface" bind:this={container}></div>

  <div class="badges">
    <span class="num">{number}</span>
    <span class="label">{tile.angle.label}</span>
    {#if hasAudio}<span class="badge audio" title="Tonquelle">♪</span>{/if}
    {#if tile.angle.role === 'audio'}<span class="badge">nur Ton</span>{/if}
    {#if tile.angle.role === 'video'}<span class="badge">stumm</span>{/if}
    {#if offsetLabel}<span class="badge offset" title="Sync-Verzögerung">{offsetLabel}</span>{/if}
    {#if tile.state === 'playing' && tile.error}
      <!-- The picture still runs; only the quality switch failed and is retried. -->
      <span class="badge warn" title={tile.error}>⚠ Qualität</span>
    {/if}
    {#if erregt}
      <span class="badge hot" title="Tonpegel über dem Normalpegel dieser Perspektive">
        +{erregung.toFixed(0)} dB
      </span>
    {/if}
  </div>

  {#if tile.state === 'loading'}
    <div class="status"><span class="spinner"></span> lädt …</div>
  {:else if tile.state === 'error'}
    <div class="status error">
      <strong>Fehler</strong>
      <span class="detail">{tile.error ?? 'unbekannt'}</span>
    </div>
  {/if}

  {#if showStats && tile.stats}
    <div class="stats mono">
      {tile.stats.width}×{tile.stats.height}
      · {tile.stats.mode ?? '—'}
      · drop {tile.stats.droppedFrames}
      · buf {tile.stats.bufferSeconds.toFixed(1)}s
      · lat {tile.stats.latencySeconds.toFixed(1)}s
    </div>
  {/if}
</div>

<style>
  .tile {
    position: relative;
    flex: 1;
    overflow: hidden;
    background: #000;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    min-width: 0;
    min-height: 0;
  }

  /* Visible from across a room without reading anything. */
  .tile.hot { border-color: var(--danger); }

  /* The main picture is what is being watched: no frame around it, not even a
     red one -- the cheer badge is enough there. */
  .tile.main, .tile.main.hot { border-color: transparent; }

  /* A mouse click focuses the tile too; the global focus ring is meant for the
     remote, not for that. */
  :global(main:not(.tv)) .tile:focus:not(:focus-visible) { outline: none; }

  .surface { position: absolute; inset: 0; }

  /* A compact chip in the corner rather than a bar across the tile: on a small
     tile a full-width strip covered a good part of the picture. */
  .badges {
    position: absolute;
    left: 0.35em; top: 0.35em;
    display: flex;
    align-items: center;
    gap: 0.35em;
    max-width: calc(100% - 0.7em);
    padding: 0.2em 0.45em 0.2em 0.2em;
    font-size: var(--tile-label-size);
    line-height: 1.2;
    background: rgba(0, 0, 0, 0.55);
    border-radius: 0.4em;
    pointer-events: none;
  }

  .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4em;
    height: 1.4em;
    border-radius: 0.25em;
    background: rgba(255, 255, 255, 0.16);
    font-variant-numeric: tabular-nums;
  }

  .label {
    min-width: 0;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .badge {
    padding: 0.1em 0.4em;
    border-radius: 0.25em;
    background: rgba(255, 255, 255, 0.12);
    font-size: 0.85em;
    white-space: nowrap;
  }

  .badge.audio { background: var(--audio); color: #04101d; font-weight: 700; }
  .badge.offset { background: var(--warn); color: #241a00; font-weight: 600; }
  .badge.warn { background: rgba(255, 196, 0, 0.25); color: var(--warn); }
  .badge.hot { background: var(--danger); color: #fff; font-weight: 700; }

  .status {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: rgba(0, 0, 0, 0.55);
    font-size: var(--font-sm);
    text-align: center;
    padding: var(--pad);
  }

  .status.error { color: var(--danger); }
  .status .detail { color: var(--text-dim); font-size: 0.85em; max-width: 90%; }

  .spinner {
    width: 1.1em; height: 1.1em;
    border: 2px solid var(--border-strong);
    border-top-color: var(--text);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .stats {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    padding: 4px 8px;
    font-size: 0.72em;
    background: rgba(0, 0, 0, 0.7);
    color: var(--text-dim);
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
  }
</style>
